import { LinkOutlined } from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history, Link } from '@umijs/max';
import React from 'react';
import { App } from 'antd';
import {
  AvatarDropdown,
  AvatarName,
  Footer,
  UserAvatar,
} from '@/components';
import { currentUser as queryCurrentUser } from '@/services/ant-design-pro/api';
import defaultSettings from '../config/defaultSettings';
import { isWechatBindingPublicRoute } from '@/constants/wechatBindingPublic';
import { filterMenusByPermission, clearDebugStorage, setUserPermissions, getUserInfo, setUserInfo, clearAuthInfo, getUserPermissions, ensureValidAccessToken, refreshAccessToken, tryRestoreSession } from '@/utils/auth';
import WechatBindingChecker from '@/components/WechatBindingChecker';
import PendingContentChecker from '@/components/PendingContentChecker';
// 移除未读通知弹窗（已有待处理提醒弹窗即可）
import '@ant-design/v5-patch-for-react-19';
import './styles/fonts.css';

// 只在开发环境显示配置栏，生产环境不显示
const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  // 清理调试相关的本地存储项
  clearDebugStorage();

  // 检查是否为登录相关页面或公开页面
  const isLoginPage = [loginPath, '/user/register', '/user/register-result'].includes(location.pathname);
  const isPublicPage = isWechatBindingPublicRoute(
    typeof location !== 'undefined' ? location.pathname : undefined,
  );

  // 调试日志
  if (isPublicPage) {
    console.log('🔍 getInitialState: 检测到公开页面，跳过用户信息获取', location.pathname);
  }

  // 登录页面和公开页面不加载任何配置，避免API调用
  if (isLoginPage || isPublicPage) {
    // 对于授权页面，确保不会触发任何用户信息获取
    return {
      fetchUserInfo: undefined,
      currentUser: undefined, // 明确设置为undefined，避免后续检查
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }

  const fetchUserInfo = async () => {
    try {
      // 优先尝试恢复登录态，支持用户在持续操作时自动续期
      if (!(await tryRestoreSession())) {
        clearAuthInfo();
        // 只有在非登录页面且非公开页面时才跳转到登录页
        const currentPath = history.location.pathname;
        const isPublic = isWechatBindingPublicRoute(currentPath);
        if (currentPath !== loginPath && !isPublic) {
          history.push(loginPath);
        }
        return undefined;
      }

      // 尝试从本地存储获取用户信息
      const localUserInfo = getUserInfo();
      if (localUserInfo) {
        // 转换为API.CurrentUser格式
        const currentUser = {
          name: localUserInfo.name,
          avatar: localUserInfo.avatar,
          userid: localUserInfo.id.toString(),
          email: localUserInfo.email,
          title: localUserInfo.title,
          mainAccount: localUserInfo.mainAccount,
        } as API.CurrentUser;

        // 检查权限数据是否有效
        const permissions = getUserPermissions();
        if (!permissions || !permissions.menus || !Array.isArray(permissions.menus)) {
          // 如果权限数据无效，从服务器重新获取
          const msg = await queryCurrentUser({
            skipErrorHandler: true,
          });

          // 如果服务器返回了权限数据，保存到本地存储
          if (msg.data && msg.data.permissions) {
            setUserPermissions(msg.data.permissions);
          }

          return msg.data;
        }

        return currentUser;
      }

      // 如果本地没有用户信息，从服务器获取
      const msg = await queryCurrentUser({
        skipErrorHandler: true,
      });

      // 如果服务器返回了权限数据，保存到本地存储
      if (msg.data && msg.data.permissions) {
        setUserPermissions(msg.data.permissions);
      }

      // 保存完整的用户信息到 localStorage（仅用于UI展示，不用于权限验证）
      // ⚠️ 安全说明：localStorage 中的数据可以被用户修改，不能作为安全依据
      // 所有权限验证都在后端进行
      if (msg.data) {
        // 保存完整信息到一个单独的 key，便于调试（但不应依赖此数据做权限判断）
        localStorage.setItem('full_user_info', JSON.stringify(msg.data));

        // 尝试更新 user_info（如果存在）
        const existingUserInfo = getUserInfo();
        const userData = msg.data as any;
        if (existingUserInfo && userData?.id) {
          // 合并现有信息和新信息（只更新可用的字段），包括 account_type
          const accountType = userData.account_type || userData.accountType || existingUserInfo.accountType;
          const updatedUserInfo: any = {
            ...existingUserInfo,
            id: userData.id || existingUserInfo.id,
            username: userData.username || existingUserInfo.username,
            name: userData.name || userData.nickname || existingUserInfo.name,
            email: userData.email || existingUserInfo.email,
            avatar: userData.avatar || existingUserInfo.avatar,
            title: userData.title || existingUserInfo.title,
            mainAccount: userData.mainAccount !== undefined ? userData.mainAccount : existingUserInfo.mainAccount,
            accountType: accountType,
            accountId: userData.account_id || userData.accountId || existingUserInfo.accountId,
            accountName: userData.account_name || userData.accountName || existingUserInfo.accountName,
            isSubAccount: userData.is_sub_account !== undefined ? userData.is_sub_account : (userData.isSubAccount !== undefined ? userData.isSubAccount : existingUserInfo.isSubAccount),
            access: userData.access || existingUserInfo.access || null,
            role_id: userData.role_id ?? existingUserInfo.role_id ?? null,
            roleId: userData.roleId ?? userData.role_id ?? existingUserInfo.roleId ?? existingUserInfo.role_id ?? null,
          };
          // 同时保存 account_type 字段（下划线命名）以便统一使用
          updatedUserInfo.account_type = accountType;
          setUserInfo(updatedUserInfo);
        } else if (userData?.id) {
          // 如果不存在现有用户信息，直接保存新信息
          const accountType = userData.account_type || userData.accountType || null;
          const newUserInfo: any = {
            id: userData.id,
            username: userData.username || '',
            name: userData.name || userData.nickname || '',
            email: userData.email || '',
            avatar: userData.avatar || '/static/default-avatar.png',
            department: userData.department || null,
            title: userData.title || '',
            mainAccount: userData.mainAccount ?? 0,
            accountType: accountType,
            accountId: userData.account_id || userData.accountId || null,
            accountName: userData.account_name || userData.accountName || null,
            isSubAccount: userData.is_sub_account || userData.isSubAccount || false,
            access: userData.access || null,
            role_id: userData.role_id ?? null,
            roleId: userData.roleId ?? userData.role_id ?? null,
          };
          // 同时保存 account_type 字段（下划线命名）以便统一使用
          newUserInfo.account_type = accountType;
          setUserInfo(newUserInfo);
        }
      }

      return msg.data;
    } catch (_error) {
      console.error('🔍 获取用户信息失败:', _error);
      clearAuthInfo();
      // 只有在非登录页面且非公开页面时才跳转到登录页
      const currentPath = history.location.pathname;
      const isPublic = isWechatBindingPublicRoute(currentPath);
      if (currentPath !== loginPath && !isPublic) {
        history.push(loginPath);
      }
    }
    return undefined;
  };

  // 对于公开页面和登录页面，不执行fetchUserInfo，避免触发登录检查
  if (isLoginPage || isPublicPage) {
    return {
      fetchUserInfo: undefined,
      currentUser: undefined, // 明确设置为undefined，避免后续检查
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }

  // 对于其他页面，执行fetchUserInfo
  const currentUser = await fetchUserInfo();
  return {
    fetchUserInfo,
    currentUser,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  // 使用固定配置，避免在布局中调用API
  const systemLogo = '/logo.png';
  const systemName = '教育系统新媒体综合管理平台';
  const showLoginWatermark = true;

  return {
    logo: systemLogo,
    title: systemName,
    actionsRender: () => [],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: showLoginWatermark ? {
      content: initialState?.currentUser?.name,
      gapX: 150, // 水平间距
      gapY: 100, // 垂直间距
    } : undefined,
    footerRender: () => <Footer />,
             onPageChange: () => {
               const { location } = history;
               // 如果没有登录，重定向到 login
               // 但是要排除登录相关的页面和公开页面，避免重定向循环
               const isLoginRelatedPage = [loginPath, '/user/register', '/user/register-result'].includes(location.pathname);
               const isPublicPage = isWechatBindingPublicRoute(location.pathname);

               // 如果是公开页面，直接返回，不进行任何登录检查
               if (isPublicPage) {
                 return;
               }

               // 只有在非公开页面且未登录时才跳转到登录页
               if (!initialState?.currentUser && !isLoginRelatedPage) {
                 history.push(loginPath);
               }
             },
    // 菜单权限过滤
    menuDataRender: (menuData) => {
      return filterMenusByPermission(menuData);
    },
    bgLayoutImgList: [
      {
        src: '/images/bg-layout/bg-layout-1.png',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: '/images/bg-layout/bg-layout-2.png',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: '/images/bg-layout/bg-layout-3.png',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: isDev
      ? [
          <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
            <LinkOutlined />
            <span>OpenAPI 文档</span>
          </Link>,
        ]
      : [],
    menuHeaderRender: undefined,
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <App>
          {children}
          {/* 微信绑定检查器 - 只在已登录用户中显示 */}
          {initialState?.currentUser && <WechatBindingChecker />}
          {/* 待处理内容检查器 - 只在已登录用户中显示 */}
          {initialState?.currentUser && <PendingContentChecker currentUser={initialState.currentUser} />}
          {/* 取消未读通知弹窗，避免重复提醒 */}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </App>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  baseURL: process.env.REACT_APP_API_BASE_URL || (typeof window !== 'undefined' && (window as any).APP_CONFIG?.API_BASE_URL) || (process.env.NODE_ENV === 'development' ? 'http://127.0.0.1:8000' : '//api.nem.720dt.com'),
  requestInterceptors: [
    async (config: any) => {
      // 添加认证token
      const token = await ensureValidAccessToken();
      config.headers = config.headers || {};
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      // 设置Content-Type
      if (config.method === 'post' || config.method === 'put') {
        config.headers['Content-Type'] = 'application/json';
      }
      return config;
    },
  ],
  responseInterceptors: [
    (response: any) => {
      // 如果是blob响应，直接返回，不进行JSON处理
      if (response.config?.responseType === 'blob' || response.data instanceof Blob) {
        return response;
      }

      // 处理响应数据，统一使用 code 字段判断成功/失败
      const { data } = response;

      if (data && typeof data === 'object' && (data.code !== undefined || data.success !== undefined)) {
        // 使用 code 或 success 字段判断成功/失败
        if (data.code === 200 || data.success === true) {
          // 成功：返回 data 字段内容
          // 如果 data.data 存在，返回 data.data；否则返回整个 data 对象
          const responseData = data.data !== undefined ? data.data : data;
          return { ...response, data: responseData };
        } else {
          // 检查是否是登录请求
          const isLoginRequest = response.config?.url?.includes('/login');

          if (isLoginRequest) {
            // 登录失败：不抛出异常，返回错误信息让登录页面处理
            return {
              ...response,
              data: {
                success: false,
                message: data.message || '用户名或密码错误',
                isLoginError: true
              }
            };
          }
          // 若请求方声明 skipErrorHandler，将完整响应返回给调用方自行处理（如导入校验失败+下载详情）
          if (response.config?.skipErrorHandler) {
            return { ...response, data };
          }
          // 其他请求失败：抛出错误
          throw new Error(data.message || '请求失败');
        }
      }

      return response;
    },
  ],
  // 错误处理 - 自动token刷新
  errorConfig: {
    errorHandler: async (error: any) => {
      console.error('请求错误:', error);

      if (error.response?.status === 401) {
        const isRefreshRequest = error.config?.url?.includes('/api/refreshToken');

        // Token过期，尝试刷新
        if (!isRefreshRequest) {
          try {
            const refreshedTokenInfo = await refreshAccessToken();
            if (refreshedTokenInfo) {
              // 重试原请求
              const originalRequest = error.config;
              originalRequest.headers = {
                ...originalRequest.headers,
                Authorization: `Bearer ${refreshedTokenInfo.access_token}`,
              };
              return fetch(originalRequest.url, originalRequest);
            }
          } catch (refreshError) {
            console.error('Token刷新失败:', refreshError);
          }
        }

        // 刷新失败或没有refresh_token；微信授权公开页不跳转登录，避免手机扫码后进登录页
        clearAuthInfo();
        if (typeof window !== 'undefined' && isWechatBindingPublicRoute(window.location.pathname)) {
          return Promise.reject(error);
        }
        window.location.href = '/user/login';
        return;
      }

      // 其他错误处理
      if (error.response) {
        const { status, data } = error.response;
        switch (status) {
          case 403:
            console.error('权限不足');
            break;
          case 404:
            console.error('请求的资源不存在');
            break;
          case 500:
            console.error('服务器内部错误');
            break;
          default:
            console.error(`请求失败: ${status}`);
        }
      }

      // 确保函数有返回值
      return Promise.reject(error);
    },
  }
};
